/* ------------------------------------------------------------------
  File: Breadcrumb.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable breadcrumb navigation component for the Accend Holdings website.
//   - Supports React Router links, external links, current page state,
//     custom separators, optional home item, and dark-surface compatibility.
//   - Uses the acc-breadcrumb namespace to avoid conflicts with Bootstrap.
//   - Suitable for nested pages such as Business, Investors, Newsroom,
//     Careers, Resources, and detail pages.

/**
 * Breadcrumb
 *
 * A reusable breadcrumb navigation component.
 *
 * @param {Array} items
 * Array of breadcrumb items.
 *
 * Each item should support:
 * - label: string
 * - to: string
 * - href: string
 * - current: boolean
 *
 * @param {React.ReactNode} separator
 * Optional separator displayed between breadcrumb items.
 *
 * @param {boolean} [showHome=true]
 * Controls whether the Home breadcrumb item is shown.
 *
 * @param {string} [homeLabel="Home"]
 * Label for the home breadcrumb item.
 *
 * @param {string} [homeTo="/"]
 * Internal route for the home breadcrumb item.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import { Link } from "react-router-dom";
import { LuChevronRight } from "react-icons/lu";
import "./Breadcrumb.css";

const Breadcrumb = ({
  items = [],
  separator,
  showHome = true,
  homeLabel = "Home",
  homeTo = "/",
  className = "",
  ...props
}) => {
  const breadcrumbItems = showHome
    ? [{ label: homeLabel, to: homeTo }, ...items]
    : items;

  const breadcrumbClasses = ["acc-breadcrumb", className]
    .filter(Boolean)
    .join(" ");

  const defaultSeparator = (
    <LuChevronRight className="acc-breadcrumb__separator-icon" />
  );

  if (!breadcrumbItems.length) {
    return null;
  }

  return (
    <nav className={breadcrumbClasses} aria-label="Breadcrumb" {...props}>
      <ol className="acc-breadcrumb__list">
        {breadcrumbItems.map((item, index) => {
          const isLastItem = index === breadcrumbItems.length - 1;
          const isCurrent = item.current || isLastItem;
          const separatorContent = separator || defaultSeparator;

          return (
            <li
              key={`${item.label}-${index}`}
              className="acc-breadcrumb__item"
            >
              {item.to && !isCurrent ? (
                <Link to={item.to} className="acc-breadcrumb__link">
                  {item.label}
                </Link>
              ) : item.href && !isCurrent ? (
                <a href={item.href} className="acc-breadcrumb__link">
                  {item.label}
                </a>
              ) : (
                <span
                  className="acc-breadcrumb__current"
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}

              {!isLastItem && (
                <span
                  className="acc-breadcrumb__separator"
                  aria-hidden="true"
                >
                  {separatorContent}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;